import * as React from "react";
import { cn } from "../../lib/cn";
import type { Block, BlockRegistry } from "./BlockRenderer";

/**
 * Shared "render child blocks" helper for composition blocks.
 *
 * Composition blocks (`carousel` today; `grid` / `tabs` / `accordion` later)
 * nest other blocks by reference. Rather than each re-implementing block
 * lookup + the error boundary + the safe placeholder, they resolve children
 * through this one helper, which renders each child against the SAME registry
 * flowing through `BlockRegistryContext` (provided by `BlockRenderer`). This is
 * the single code path the catalog (§5) asks for: "extract that into a shared
 * render-child-blocks helper so the composition blocks share one code path."
 *
 * A child is a `block_ref` - either an inline `Block` envelope, or a thin
 * `{ ref: <block_id> }` pointer the host resolves from a side table it passes
 * via `resolveRef`. Inline envelopes are the common case; the `ref` form exists
 * so a large block can be sent once and referenced from several containers.
 *
 * The renderer is deliberately decoupled from `BlockRenderer` (it consumes the
 * registry from context, not by importing the component) so there is no import
 * cycle: `BlockRenderer` imports the composition blocks, the composition blocks
 * import this, this imports only types + the context. SSR-safe; no `window` /
 * `document` access.
 */

/** A reference to a nested block: an inline envelope or a `{ ref }` pointer. */
export type BlockRef = Block | { ref: string };

/** Resolve a `{ ref }` pointer to a `Block`. Host-provided side table. */
export type BlockRefResolver = (id: string) => Block | undefined;

interface BlockRegistryContextValue {
  registry: BlockRegistry;
  resolveRef?: BlockRefResolver;
  /** Renders one resolved `Block` (the `BlockRenderer` core, injected to avoid a cycle). */
  renderBlock: (block: Block) => React.ReactElement;
}

const BlockRegistryContext = React.createContext<BlockRegistryContextValue | undefined>(undefined);

/** Provider set up by `BlockRenderer`; carries the merged registry + the core renderer. */
export const BlockRegistryProvider = BlockRegistryContext.Provider;

/** True when a `BlockRef` is a `{ ref }` pointer rather than an inline envelope. */
function isRefPointer(child: BlockRef): child is { ref: string } {
  return (
    typeof child === "object" &&
    child !== null &&
    "ref" in child &&
    typeof (child as { ref: unknown }).ref === "string" &&
    !("type" in child)
  );
}

/** Normalize one `BlockRef` to a `Block`, resolving `{ ref }` pointers. */
function resolveChild(child: BlockRef, resolveRef?: BlockRefResolver): Block | undefined {
  if (isRefPointer(child)) return resolveRef?.(child.ref);
  if (child && typeof child === "object" && "type" in child) return child as Block;
  return undefined;
}

export interface BlockChildrenProps {
  /** The nested `block_ref`s to render in order. */
  items: BlockRef[];
  /** Wrapper className for the children container. */
  className?: string;
  /**
   * Wrap each rendered child. Composition blocks use this to drop a child into
   * a carousel cell, a grid track, a tab panel, etc. Receives the child element
   * and its index. Defaults to identity (render the child as-is).
   */
  renderItem?: (child: React.ReactNode, index: number, block: Block | undefined) => React.ReactNode;
}

/**
 * Render a list of nested `block_ref`s through the ambient registry. Renders a
 * small inert note (never throws) when used outside a `BlockRenderer` tree or
 * when a `{ ref }` cannot be resolved, so a malformed composition block
 * degrades gracefully like every other block.
 */
export function BlockChildren({ items, className, renderItem }: BlockChildrenProps) {
  const ctx = React.useContext(BlockRegistryContext);

  if (!ctx) {
    // Used outside a BlockRenderer tree - cannot resolve the registry. Inert.
    return (
      <div
        role="note"
        className={cn(
          "rounded-lg border border-dashed border-border bg-muted/30 p-2 text-xs text-muted-foreground",
        )}
        data-slot="block-children-detached"
      >
        Nested blocks need a BlockRenderer context to render.
      </div>
    );
  }

  const { resolveRef, renderBlock } = ctx;

  const rendered = items.map((child, index) => {
    const block = resolveChild(child, resolveRef);
    const node = block ? (
      renderBlock(block)
    ) : (
      <div
        role="note"
        className="rounded-lg border border-dashed border-border bg-muted/30 p-2 text-xs text-muted-foreground"
        data-slot="block-child-unresolved"
      >
        Can't resolve nested block.
      </div>
    );
    const key = block?.block_id ?? `child-${index}`;
    const wrapped = renderItem ? renderItem(node, index, block) : node;
    return <React.Fragment key={key}>{wrapped}</React.Fragment>;
  });

  if (className) {
    return (
      <div className={className} data-slot="block-children">
        {rendered}
      </div>
    );
  }
  return <>{rendered}</>;
}
